import { useTheme } from '@mui/material';
import { TaxExeptionStatusResponse } from '~/adapters/services/accounts/tax-exemptions-ECMS-old/types';

export type TaxExemptionInternalStatus = 'Approved' | 'In Review' | 'Renewal In Progress' | 'Archived' | 'Rejected';

interface StatusTranslateResult {
    status: TaxExemptionInternalStatus | TaxExeptionStatusResponse;
    color: string;
    tooltipMessage?: string;
}

export const useStatusTranslate = () => {
    const theme = useTheme();

    const translator: Record<TaxExeptionStatusResponse, StatusTranslateResult | undefined> = {
        complete: {
            status: 'Approved',
            color: theme.palette.success.main,
            tooltipMessage: 'You are able to make qualifying tax-exempt purchases in this state.',
        },
        pending_file: {
            status: 'More Info Needed',
            color: theme.palette.warning.main,
            tooltipMessage:
                'We need additional information for your application. Please update your application in the portal.',
        },
        incomplete: {
            status: 'Application Incomplete',
            color: theme.palette.warning.main,
            tooltipMessage:
                'We need additional information for your application. Please update your application in the portal.',
        },
        pending_review: {
            status: 'In Review',
            color: theme.palette.info.main,
            tooltipMessage:
                'We’re reviewing your documents and will update your status soon, typically within 2-3 business days.',
        },
        pending_renewal: {
            status: 'Renewal In Progress',
            color: theme.palette.info.main,
            tooltipMessage:
                'We’re reviewing your renewal documents and will update your status soon, typically within 2-3 business days.',
        },
        pending_renewal_response: {
            status: 'Renewal In Progress',
            color: theme.palette.error.main,
            tooltipMessage: 'We need more information for your renewal. Please review your status in the portal.',
        },
        archived: {
            status: 'Archived',
            color: theme.palette.error.main,
            tooltipMessage:
                'This certificate is no longer valid. Please add a new certificate to update your tax-exempt status.',
        },
        rejected_pending_file: {
            status: 'Denied',
            color: theme.palette.error.main,
            tooltipMessage:
                'Your tax exemption status cannot be verified. If you think this is an error, start a new application in the portal.',
        },
        rejected: {
            status: 'Denied',
            color: theme.palette.error.main,
            tooltipMessage:
                'Your tax exemption status cannot be verified. If you think this is an error, start a new application in the portal.',
        },
        rejected_pending_review: {
            status: 'Denied',
            color: theme.palette.error.main,
            tooltipMessage:
                'Your tax exemption status cannot be verified. If you think this is an error, start a new application in the portal.',
        },
        pending_request: {
            status: 'More Info Needed',
            color: theme.palette.error.main,
            tooltipMessage:
                'We need additional information for your application. Please review your application in the portal.',
        },
    };

    return (ttrStatus: TaxExeptionStatusResponse): StatusTranslateResult => {
        return (
            translator[ttrStatus] || {
                status: ttrStatus.replace('_', ' '),
                color: theme.palette.info.main,
            }
        );
    };
};
